import React, { useEffect, useRef, useState } from 'react';
import { read, utils } from 'xlsx';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { imageUrl } from '../helper';
import { BombiixModal } from '../BombiixModal';
import { CollapseIcon, ExpandIcon } from '../../bombiix-icon';

/* Serve the pdf.js worker as a static asset copied from node_modules by scripts/copyPdfWorker.js
   (see package.json prestart/prebuild) — never load executable JS from a third-party CDN. Routing
   this file through webpack's own module system (e.g. `new URL(..., import.meta.url)`) wraps it in
   a require()/module interop shim that breaks when pdfjs loads it as a raw Worker script. */
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLS_TYPE = 'application/vnd.ms-excel';
const ODS_TYPE = 'application/vnd.oasis.opendocument.spreadsheet';
const PPT_TYPE = 'application/vnd.ms-powerpoint';
const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const WORD_TYPE = 'application/msword';
const PDF_TYPE = 'application/pdf';
/** `.ai` (Adobe Illustrator) files are PDF-compatible, so pdf.js renders them from the raw upload. */
const ILLUSTRATOR_TYPES = ['application/postscript', 'application/illustrator'];
const PDF_RENDERED_EXTENSIONS = ['pdf', 'ai'];
const IMAGE_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'];
const S3_KEY_PREVIEW_MODULES = ['SpecDocument', 'specTemplate', 'manualDocument'];
/** SheetJS reads legacy BIFF (.xls) and OpenDocument as well as .xlsx, so all three share one branch. */
const SPREADSHEET_TYPES = [XLSX_TYPE, XLS_TYPE, ODS_TYPE];
/** Uploads frequently arrive as `application/octet-stream`, so the file name decides when the mime type is generic. */
const SPREADSHEET_EXTENSIONS = ['xls', 'xlsx', 'ods'];

/** Lower-cased extension of a file name — empty string when the name carries none. */
const fileExtensionOf = (fileName?: string): string => {
  const parts = (fileName ?? '').split('.');
  return parts.length > 1 ? (parts[parts.length - 1] ?? '').toLowerCase() : '';
};

type SheetStatus = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * Parse the first sheet of a workbook buffer into plain string rows.
 * `header: 1` returns raw row arrays, so a sheet whose first row is a title (not column names) still
 * renders every data row instead of collapsing to an empty object list.
 */
const parseFirstSheet = (arrayBuffer: ArrayBuffer): string[][] => {
  const workbook = read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
  if (!sheet) throw new Error('The workbook has no readable sheet.');
  const rows = utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', blankrows: false, raw: false });
  return rows.map((row) => row.map((cell) => String(cell ?? '')));
};

interface PreviewData {
  document?: string;
  s3Key?: string;
  mimetype?: string;
  mimeType?: string;
  fileType?: string;
  fileName?: string;
}

export interface BombiixPreviewModalProps {
  onOpen: boolean;
  toggle: () => void;
  data?: PreviewData;
  moduleName: string;
}

/** Vendor-prefixed fullscreen members are missing from the standard DOM lib types. */
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};
type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => void;
};

/**
 * Document preview modal (Excel / PDF / PowerPoint / Word / image) rendered inside the master-design
 * `BombiixModal` chrome — close-only header bar, centered file-name title, `--preview` width. TypeScript
 * port of `ModalPopup/PreviewModal.js`; S3 URL resolution is unchanged, but spreadsheet and PDF detection
 * also fall back to the file extension because uploads often carry a generic `application/octet-stream`.
 */
const BombiixPreviewModal: React.FC<BombiixPreviewModalProps> = ({ onOpen, toggle, data, moduleName }) => {
  const { t } = useTranslation('common');
  const [sheetRows, setSheetRows] = useState<string[][]>([]);
  const [sheetStatus, setSheetStatus] = useState<SheetStatus>('idle');
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfLoadFailed, setPdfLoadFailed] = useState(false);
  const [pdfPageWidth, setPdfPageWidth] = useState<number | null>(null);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const getSlugName = (): string | null => {
    try {
      const { hostname } = new URL(window.location.origin);
      return hostname.split('.')[0] ?? null;
    } catch {
      return null;
    }
  };

  const filePreview = (element?: string): string => {
    if (!element) return '';
    const baseUrl = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com`;
    if (moduleName === 'manualDocument') {
      const fullPath = `${getSlugName()}/${process.env.REACT_APP_MANUAL_DOCUMENT}${element}`;
      return `${baseUrl}/${fullPath.split('/').map((part) => encodeURIComponent(part)).join('/')}`;
    }
    return `${baseUrl}/${element.split('/').map((part) => encodeURIComponent(part)).join('/')}`;
  };

  const renderImage = (element: PreviewData): string => {
    if (moduleName === 'SpecDocument' && element?.document) return filePreview(element.document);
    if (moduleName === 'specTemplate' && element?.s3Key) return filePreview(element.s3Key);
    if (moduleName === 'manualDocument' && element?.s3Key) return filePreview(element.s3Key);
    if (element?.document?.includes('s3.eu-west-2')) return element.document || '';
    return `${imageUrl}/${element}#toolbar=0`;
  };

  const extension = fileExtensionOf(data?.fileName);
  const declaredTypes = [data?.mimetype, data?.mimeType, data?.fileType];
  const isSpreadsheet = declaredTypes.some((type) => Boolean(type) && SPREADSHEET_TYPES.includes(type ?? '')) || SPREADSHEET_EXTENSIONS.includes(extension);
  const isPdf =
    declaredTypes.some((type) => type === PDF_TYPE || ILLUSTRATOR_TYPES.includes(type ?? '')) || PDF_RENDERED_EXTENSIONS.includes(extension);
  const isPpt = data?.mimeType === PPT_TYPE || data?.mimeType === PPTX_TYPE || data?.fileType === PPTX_TYPE || data?.fileType === PPT_TYPE;
  const isWord = data?.mimeType === WORD_TYPE || data?.fileType === WORD_TYPE;
  const sourceUrl = S3_KEY_PREVIEW_MODULES.includes(moduleName) ? filePreview(data?.s3Key) : data?.document ?? '';
  const pdfUrl = sourceUrl;
  const noPreview = <div className="text-center">{t('PREVIEW_MODALS.NO_PREVIEW_AVAILABLE')}</div>;

  useEffect(() => {
    if (!isSpreadsheet) return undefined;
    let cancelled = false;
    /* Read the workbook client-side: S3 serves the raw file, SheetJS turns sheet 1 into rows. */
    const fetchSheetData = async (): Promise<void> => {
      setSheetStatus('loading');
      try {
        if (!sourceUrl) throw new Error('Preview source URL is missing.');
        const response = await fetch(sourceUrl);
        if (!response.ok) throw new Error(`Preview request failed with status ${response.status}.`);
        const rows = parseFirstSheet(await response.arrayBuffer());
        if (cancelled) return;
        setSheetRows(rows);
        setSheetStatus('ready');
      } catch {
        if (cancelled) return;
        setSheetRows([]);
        setSheetStatus('failed');
      }
    };
    fetchSheetData();
    return () => {
      cancelled = true;
    };
  }, [isSpreadsheet, sourceUrl]);

  useEffect(() => {
    setPdfLoadFailed(false);
    setNumPages(0);
  }, [data?.s3Key, data?.document]);

  useEffect(() => {
    const onFsChange = (): void => {
      const fsDoc = document as FullscreenDocument;
      setIsEnlarged(Boolean(fsDoc.fullscreenElement || fsDoc.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  useEffect(() => {
    if (!pdfContainerRef.current) return undefined;
    const observer = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        const entry = entries[0];
        if (entry) setPdfPageWidth(entry.contentRect.width);
      });
    });
    observer.observe(pdfContainerRef.current);
    return () => observer.disconnect();
  }, [onOpen, isPdf, isEnlarged]);

  const onDocumentLoadSuccess = ({ numPages: loadedPages }: { numPages: number }): void => {
    setPdfLoadFailed(false);
    setNumPages(loadedPages);
  };

  const onDocumentLoadError = (): void => {
    setPdfLoadFailed(true);
    setNumPages(0);
  };

  if (isPdf) {
    const handleClose = (): void => {
      const fsDoc = document as FullscreenDocument;
      if (fsDoc.fullscreenElement) fsDoc.exitFullscreen();
      else if (fsDoc.webkitFullscreenElement) fsDoc.webkitExitFullscreen?.();
      setIsEnlarged(false);
      toggle();
    };

    const handleToggleEnlarge = (): void => {
      const fsDoc = document as FullscreenDocument;
      const fsRoot = document.documentElement as FullscreenElement;
      if (isEnlarged) {
        if (fsDoc.exitFullscreen) fsDoc.exitFullscreen();
        else fsDoc.webkitExitFullscreen?.();
      } else if (fsRoot.requestFullscreen) fsRoot.requestFullscreen();
      else fsRoot.webkitRequestFullscreen?.();
    };

    return (
      <BombiixModal
        show={onOpen}
        title={data?.fileName ?? ''}
        onClose={handleClose}
        hideSave
        size="lg"
        className={`bombiix-modal--preview pdf-preview-modal${isEnlarged ? ' pdf-enlarged bombiix-modal--preview-full' : ''}`}
      >
        {pdfUrl && (
          <div className="d-flex justify-content-end mb-2">
            <button type="button" onClick={handleToggleEnlarge} aria-label="Toggle enlarged view" className="btn p-0 d-inline-flex align-items-center">
              {isEnlarged ? <CollapseIcon size={20} /> : <ExpandIcon size={20} />}
            </button>
          </div>
        )}
        <div className="max-h-pdf" ref={pdfContainerRef}>
          {(!data || !pdfUrl || pdfLoadFailed) && noPreview}
          {pdfUrl && !pdfLoadFailed && (
            <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onDocumentLoadError}>
              {Array.from({ length: numPages }, (_, index) => (
                <div key={`page_${index + 1}`} className="pdf-page-wrapper">
                  <Page pageNumber={index + 1} width={pdfPageWidth || undefined} />
                </div>
              ))}
            </Document>
          )}
        </div>
      </BombiixModal>
    );
  }

  if (isSpreadsheet) {
    const [headerRow, ...bodyRows] = sheetRows;
    const columnCount = sheetRows.reduce((widest, row) => Math.max(widest, row.length), 0);
    return (
      <BombiixModal show={onOpen} title={data?.fileName ?? ''} onClose={toggle} hideSave size="lg" className="bombiix-modal--preview">
        {sheetStatus === 'loading' && <div className="text-center">{t('bulk-edit:PREVIEW_MODALS.LOADING')}</div>}
        {(!data || sheetStatus === 'failed' || (sheetStatus === 'ready' && sheetRows.length === 0)) && noPreview}
        {sheetStatus === 'ready' && sheetRows.length > 0 && (
          <div className="bombiix-modal__preview-sheet">
            <table className="table table-striped">
              <thead className="thead-dark">
                <tr>
                  {Array.from({ length: columnCount }, (_, columnIndex) => (
                    <th key={columnIndex}>{headerRow?.[columnIndex] ?? ''}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Array.from({ length: columnCount }, (_, columnIndex) => (
                      <td key={columnIndex}>{row[columnIndex] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </BombiixModal>
    );
  }

  if (isPpt) {
    return (
      <BombiixModal show={onOpen} title={data?.fileName ?? ''} onClose={toggle} hideSave size="lg" className="bombiix-modal--preview">
        {!data && noPreview}
        <iframe
          title="ppt"
          src={`https://docs.google.com/gview?url=${S3_KEY_PREVIEW_MODULES.includes(moduleName) ? filePreview(data?.s3Key) : data?.document}&embedded=true`}
          width="100%"
          height="100%"
          className="max-ppt-doc"
        />
      </BombiixModal>
    );
  }

  if (isWord) {
    return (
      <BombiixModal show={onOpen} title={data?.fileName ?? ''} onClose={toggle} hideSave size="lg" className="bombiix-modal--preview">
        {!data && noPreview}
        <iframe
          title="word"
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${S3_KEY_PREVIEW_MODULES.includes(moduleName) ? filePreview(data?.s3Key) : data?.document}`}
          width="100%"
          height="100%"
          className="max-ppt-doc"
        />
      </BombiixModal>
    );
  }

  const isImage = IMAGE_TYPES.includes(data?.mimeType ?? '') || IMAGE_TYPES.includes(data?.fileType ?? '');
  const canShowImage = isImage && Boolean(data?.s3Key || data?.document);

  return (
    <BombiixModal show={onOpen} title={data?.fileName ?? ''} onClose={toggle} hideSave size="lg" className="bombiix-modal--preview">
      <div className="text-center">
        {canShowImage ? (
          <img className="img-fluid max-h-80" alt="" src={moduleName === 'SpecDocument' ? filePreview(data?.document) : renderImage(data ?? {})} />
        ) : (
          noPreview
        )}
      </div>
    </BombiixModal>
  );
};

export default BombiixPreviewModal;
